import { Api } from './axios-config';

export interface CreateSubscriptionRequest {
  businessId: number;
  planSlug: string;
  billingCycle: 'monthly' | 'yearly';
}

export interface CreateSubscriptionResponse {
  checkoutUrl: string;
  mpSubscriptionId: string;
}

export interface UpgradePlanRequest {
  newPlanSlug: string;
  newBillingCycle: 'monthly' | 'yearly';
}

export interface DowngradePlanRequest {
  newPlanSlug: string;
}

export interface UpgradeInfo {
  businessId: number;
  subscriptionId: number;
  currentPlanSlug: string;
  currentPlanName: string;
  currentPlanPrice: number;
  billingCycle: string;
  availablePlans: Array<{
    id: number;
    name: string;
    slug: string;
    description?: string;
    monthlyPrice: number;
    yearlyPrice: number;
    yearlyDiscountPercentage?: number;
    isPopular: boolean;
    displayOrder: number;
    features: Array<{
      code: string;
      name: string;
      description?: string;
      category?: string;
      enabled: boolean;
    }>;
    limits: Array<{
      key: string;
      value: number;
    }>;
  }>;
  canDowngrade: boolean;
  daysUntilNextChange: number;
  hasPendingDowngrade: boolean;
  pendingPlanSlug?: string;
}

const createSubscription = async (
  data: CreateSubscriptionRequest
): Promise<CreateSubscriptionResponse> => {
  const { data: response } = await Api.post<CreateSubscriptionResponse>(
    '/api/mercado-pago/create-subscription',
    data
  );
  return response;
};

const cancelSubscription = async (businessId: number): Promise<void> => {
  await Api.post(`/api/mercado-pago/cancel-subscription/${businessId}`);
};

const retrySubscription = async (businessId: number): Promise<CreateSubscriptionResponse> => {
  const { data: response } = await Api.post<CreateSubscriptionResponse>(
    `/api/mercado-pago/retry-subscription/${businessId}`
  );
  return response;
};

const upgradePlan = async (businessId: number, data: UpgradePlanRequest): Promise<void> => {
  await Api.post(`/api/mercado-pago/upgrade-plan/${businessId}`, data);
};

const downgradePlan = async (businessId: number, data: DowngradePlanRequest): Promise<void> => {
  await Api.post(`/api/mercado-pago/downgrade-plan/${businessId}`, data);
};

const getUpgradeInfo = async (businessId: number): Promise<UpgradeInfo> => {
  const { data: response } = await Api.get<UpgradeInfo>(
    `/api/mercado-pago/upgrade-info/${businessId}`
  );
  return response;
};

export const mercadoPagoService = {
  createSubscription,
  cancelSubscription,
  retrySubscription,
  upgradePlan,
  downgradePlan,
  getUpgradeInfo,
};
